import { useEffect, useMemo, useRef, useState } from 'react'
import { App, Empty, Flex, Spin } from 'antd'
import { useTranslation } from 'react-i18next'
import { ApiError } from '@/api/client'
import {
  SCOPE_PAGE_SIZE,
  batchCloseAppScopes,
  closeAppScope,
  listAllOpenedScopes,
  listOpenedScopes,
  openAppScopes,
  type ScopeRef,
} from '@/api/scopes'
import { useCurrentApp } from '@/hooks/useCurrentApp'
import { appScopeId, type AppScope } from '@/types'
import BrandButton from '@/components/BrandButton'
import { PageHeader } from '@/components/ui'
import ScopeInfoCard from './ScopeInfoCard'
import ScopeToolbar from './ScopeToolbar'
import ScopeTable from './ScopeTable'
import ScopePager from './ScopePager'
import RequestAccessDrawer from './RequestAccessDrawer'
import {
  confirmBatchCloseScopes,
  confirmCloseScope,
  confirmDeleteScope,
  confirmWithdrawScope,
} from './confirmClose'
import { filterSelectionState, keepSelectedIds } from './filterSelection'

function refreshSelected(selected: Map<string, AppScope>, pageRows: AppScope[]) {
  let changed = false
  const next = new Map(selected)
  for (const row of pageRows) {
    if (!next.has(row.id)) continue
    if (row.status !== 'approved') next.delete(row.id)
    else next.set(row.id, row)
    changed = true
  }
  return changed ? next : selected
}

export default function ScopePage() {
  const { t } = useTranslation()
  const { message, modal } = App.useApp()
  const app = useCurrentApp()

  const [keyword, setKeyword] = useState('')
  const [debouncedKeyword, setDebouncedKeyword] = useState('')
  const [category, setCategory] = useState<string | undefined>()
  const [categories, setCategories] = useState<string[]>([])
  const [categoryNames, setCategoryNames] = useState<Record<string, string>>({})
  const [page, setPage] = useState(1)
  const [list, setList] = useState<AppScope[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [reloadToken, setReloadToken] = useState(0)
  const [selectedById, setSelectedById] = useState<Map<string, AppScope>>(() => new Map())
  const [knownIds, setKnownIds] = useState<string[] | null>(null)
  const [selectingAll, setSelectingAll] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const appliedKeyword = useRef('')
  const scanRef = useRef(0)

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = keyword.trim()
      if (appliedKeyword.current === next) return
      appliedKeyword.current = next
      scanRef.current += 1
      setDebouncedKeyword(next)
      setPage(1)
      setSelectedById(new Map())
      setKnownIds(null)
      setSelectingAll(false)
    }, 300)
    return () => window.clearTimeout(timer)
  }, [keyword])

  useEffect(() => {
    appliedKeyword.current = ''
    scanRef.current += 1
    setKeyword('')
    setDebouncedKeyword('')
    setCategory(undefined)
    setPage(1)
    setSelectedById(new Map())
    setKnownIds(null)
    setSelectingAll(false)
  }, [app.id])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    listOpenedScopes(app.id, {
      page,
      page_size: SCOPE_PAGE_SIZE,
      keyword: debouncedKeyword || undefined,
      category,
    })
      .then((result) => {
        if (cancelled) return
        const lastPage = Math.max(1, Math.ceil(result.total / SCOPE_PAGE_SIZE))
        if (page > lastPage) {
          setPage(lastPage)
          return
        }
        setList(result.list)
        setTotal(result.total)
        setCategories(result.categories)
        setCategoryNames(result.categoryNames)
        setLoadError(false)
        setLoading(false)
      })
      .catch(() => {
        if (cancelled) return
        setLoadError(true)
        setCategories([])
        setCategoryNames({})
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [app.id, page, debouncedKeyword, category, reloadToken])

  useEffect(() => {
    setSelectedById((prev) => refreshSelected(prev, list))
  }, [list])

  const selectedRows = useMemo(() => [...selectedById.values()], [selectedById])
  const pageApprovedIds = useMemo(
    () => list.filter((row) => row.status === 'approved').map((row) => row.id),
    [list],
  )
  const selectedIds = useMemo(() => new Set(selectedById.keys()), [selectedById])
  const header = filterSelectionState({
    total,
    pageSize: SCOPE_PAGE_SIZE,
    pageIds: pageApprovedIds,
    selectedIds,
    knownIds,
    offPageSelected: [...selectedById.keys()].some((id) => !pageApprovedIds.includes(id)),
  })
  const selectAllDisabled =
    selectingAll ||
    total === 0 ||
    (total <= SCOPE_PAGE_SIZE && pageApprovedIds.length === 0) ||
    (knownIds !== null && knownIds.length === 0)
  const showBatchBar = selectedRows.length > 0

  const reload = () => {
    scanRef.current += 1
    setSelectingAll(false)
    setKnownIds(null)
    setReloadToken((token) => token + 1)
  }

  const failMessage = (err: unknown, fallback: string) => {
    message.error(err instanceof ApiError && err.message ? err.message : fallback)
  }

  const handleCloseOne = (scope: AppScope) => {
    confirmCloseScope(modal, {
      t,
      scope,
      onOk: async () => {
        try {
          await closeAppScope(app.id, {
            scope_id: scope.scope_id,
            identity_type: scope.identity_type,
          })
          setSelectedById((prev) => {
            if (!prev.has(scope.id)) return prev
            const next = new Map(prev)
            next.delete(scope.id)
            return next
          })
          message.success(t('scope.closeSuccess'))
          reload()
        } catch (err) {
          failMessage(err, t('scope.closeFailed'))
        }
      },
    })
  }

  const handleWithdrawOne = (scope: AppScope) => {
    confirmWithdrawScope(modal, {
      t,
      scope,
      onOk: () => {
        message.info(t('scope.withdrawUnavailable'))
      },
    })
  }

  const handleDeleteOne = (scope: AppScope) => {
    confirmDeleteScope(modal, {
      t,
      scope,
      onOk: () => {
        message.info(t('scope.deleteUnavailable'))
      },
    })
  }

  const handleBatchClose = () => {
    if (selectedRows.length === 0) return
    confirmBatchCloseScopes(modal, {
      t,
      scopes: selectedRows,
      onOk: async () => {
        try {
          const result = await batchCloseAppScopes(
            app.id,
            selectedRows.map((s) => ({
              scope_id: s.scope_id,
              identity_type: s.identity_type,
            })),
          )
          const failed = result.failed.length
          const succeeded = result.succeeded.length
          if (failed === 0) {
            message.success(t('scope.batchCloseSuccess', { count: succeeded }))
            setSelectedById(new Map())
          } else {
            const failedIds = new Set(
              result.failed.map((item) => appScopeId(item.scope_id, item.identity_type)),
            )
            setSelectedById((prev) => keepSelectedIds(prev, failedIds))
            if (succeeded === 0) message.error(t('scope.batchCloseAllFailed'))
            else message.warning(t('scope.batchClosePartial', { succeeded, failed }))
          }
          reload()
        } catch (err) {
          failMessage(err, t('scope.closeFailed'))
        }
      },
    })
  }

  const clearSelection = () => {
    scanRef.current += 1
    setSelectingAll(false)
    setSelectedById(new Map())
  }

  const selectAll = async () => {
    const ticket = ++scanRef.current
    setSelectingAll(true)
    try {
      const rows = await listAllOpenedScopes(app.id, {
        keyword: debouncedKeyword || undefined,
        category,
      })
      if (ticket !== scanRef.current) return
      const approved = rows.filter((row) => row.status === 'approved')
      setSelectedById(new Map(approved.map((row) => [row.id, row])))
      setKnownIds(approved.map((row) => row.id))
    } catch (err) {
      if (ticket !== scanRef.current) return
      failMessage(err, t('scope.loadFailed'))
    } finally {
      if (ticket === scanRef.current) setSelectingAll(false)
    }
  }

  const handleOpen = async (scopes: ScopeRef[]) => {
    try {
      await openAppScopes(app.id, scopes)
      const includesUser = scopes.some((scope) => scope.identity_type === 'user')
      message.success(t(includesUser ? 'scope.openSuccessUser' : 'scope.openSuccess'))
      setDrawerOpen(false)
      reload()
    } catch (err) {
      failMessage(err, t('scope.openFailed'))
      throw err
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        padding: showBatchBar ? '20px 20px 0' : 20,
      }}
    >
      <ScopeInfoCard />
      <PageHeader
        title={t('scope.title')}
        action={
          <BrandButton onClick={() => setDrawerOpen(true)}>
            {t('scope.requestAccess')}
          </BrandButton>
        }
      />
      <ScopeToolbar
        categories={categories}
        categoryNames={categoryNames}
        category={category}
        onCategoryChange={(value) => {
          scanRef.current += 1
          setCategory(value)
          setPage(1)
          setSelectedById(new Map())
          setKnownIds(null)
          setSelectingAll(false)
        }}
        keyword={keyword}
        onKeywordChange={setKeyword}
      />
      {loadError ? (
        <Empty description={t('scope.loadFailed')} />
      ) : loading && list.length === 0 ? (
        <Flex justify="center" style={{ padding: '48px 0' }}>
          <Spin />
        </Flex>
      ) : (
        <div
          style={{
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            minHeight: 0,
            minWidth: 0,
            width: '100%',
          }}
        >
          <div style={{ flex: 1, minHeight: 0, minWidth: 0, overflow: 'hidden' }}>
            <ScopeTable
              data={list}
              categoryNames={categoryNames}
              loading={loading}
              selectedRowKeys={[...selectedById.keys()]}
              onToggleRow={(scope, selected) => {
                setSelectedById((prev) => {
                  const next = new Map(prev)
                  if (selected) next.set(scope.id, scope)
                  else next.delete(scope.id)
                  return next
                })
              }}
              onToggleAll={(selected) => {
                if (selected) void selectAll()
                else clearSelection()
              }}
              allSelected={header.all}
              someSelected={header.some}
              selectAllDisabled={selectAllDisabled}
              selectingAll={selectingAll}
              onClose={handleCloseOne}
              onWithdraw={handleWithdrawOne}
              onDelete={handleDeleteOne}
            />
          </div>
          <ScopePager
            page={page}
            pageSize={SCOPE_PAGE_SIZE}
            total={total}
            onChange={setPage}
          />
        </div>
      )}
      {showBatchBar && (
        <Flex
          align="center"
          justify="space-between"
          style={{
            position: 'sticky',
            bottom: 0,
            marginTop: 'auto',
            marginLeft: -20,
            marginRight: -20,
            padding: '12px 20px',
            background: '#fff',
            borderTop: '1px solid #E7E7E7',
            boxShadow: '0 -4px 20px 0 rgba(0, 0, 0, 0.06)',
          }}
        >
          <span style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 14, lineHeight: '22px' }}>
            {t('scope.selectedCount', { count: selectedRows.length })}
          </span>
          <Flex align="center" gap={12}>
            <BrandButton variant="outline" onClick={clearSelection}>
              {t('common.cancel')}
            </BrandButton>
            <BrandButton variant="danger" onClick={handleBatchClose}>
              {t('scope.batchClose')}
            </BrandButton>
          </Flex>
        </Flex>
      )}
      <RequestAccessDrawer
        open={drawerOpen}
        appId={app.id}
        onClose={() => setDrawerOpen(false)}
        onSubmit={handleOpen}
      />
    </div>
  )
}
